import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '@radix-ui/react-dropdown-menu';
import { SurveyToast } from 'src/redux/survey/survey.types';
import { useUpdateToastMutation } from '../../redux/survey/survey.endpoints';
import LogoUploader from '../survey-preview-modal/components/logo-uploader';
import { PixelInput } from '../pixel-input/pixel-input';

interface ToastEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  toast: SurveyToast | null;
  onToastUpdated: () => void;
}

const ToastEditDialog: React.FC<ToastEditDialogProps> = ({ isOpen, onClose, toast, onToastUpdated }) => {
  const [updateToast] = useUpdateToastMutation();
  const [formData, setFormData] = useState<SurveyToast | null>(toast);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);

  useEffect(() => {
    setFormData(toast);
  }, [toast]);

  useEffect(() => {
    setIsSaveButtonDisabled(!formData?.name || !formData?.title);
  }, [formData]);

  const handleImageUpload = (uploadedToast: Partial<SurveyToast>) => {
    if (formData) {
      setFormData(prev => prev ? ({
        ...prev,
        ...uploadedToast,
      }) : null);
    }
  };

  const handleSubmit = async() => {
    if (!toast || !formData) return;

    try {
      await updateToast(formData).unwrap();
      onToastUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating toast:', error);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <DialogContent className="sm:max-w-[600px] bg-white">
        <DialogHeader>
          <DialogTitle>
            Edit Toast
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {[
            { id: 'name', label: 'Name', required: true },
            { id: 'title', label: 'Title', required: true },
            { id: 'subtitle', label: 'Subtitle' },
            { id: 'button_copy', label: 'Button Text' },
            { id: 'button_url', label: 'Button URL' },
            { id: 'custom_tag', label: 'Custom Tag' },
          ].map(({ id, label, required }) => (
            <div
              key={id}
              className="grid grid-cols-4 items-center gap-4 my-2"
            >
              <Label className="text-right">
                {label}
                {required && '*'}
              </Label>
              <Input
                id={id}
                className="col-span-3"
                value={formData?.[id as keyof typeof formData] || ''}
                onChange={(e) => setFormData(prev => prev ? { ...prev, [id]: e.target.value } : null)}
              />
            </div>
          ))}

          <div className="grid grid-cols-4 items-center gap-4 my-2">
            <Label className="text-right">
              Pixel
            </Label>
            <div className="col-span-3">
              <PixelInput
                initialPixel={formData?.pixel || ''}
                onSave={(value) => setFormData(prev => prev ? { ...prev, pixel: value } : null)}
                showToggle={false}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">
              Toast Image
            </Label>
            <div className="col-span-3">
              <LogoUploader
                src={formData?.image_url || null}
                type="toast"
                toast={toast}
                handleEditToast={handleImageUpload}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={isSaveButtonDisabled}
          >
            Save Toast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ToastEditDialog;